/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';
import { GlobalHeaderBladeType, GlobalSettingsBladeTabsType } from 'src/appShell/globalHeader/types';
import { CluBuildSchemaPagePivot } from 'src/clu/appShell/cluBuildSchemaPage/CluBuildSchemaPage';
import { logLocaleChanged } from 'src/core/services/telemetry/eventsNamesTemplate';
import { Allowlist, getAzureAppConfigAllowlist } from 'src/core/utils/allowlistUtil';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl, Portal } from 'src/core/utils/routingUtils';
import { Locale } from 'src/core/utils/supportedLocales';
import { LandingPagePivot } from 'src/languageStudio/appShell/landingPage/components/DemoTileSection';
import { TeachingBubblesKeys } from 'src/luis/projects/events/types/TeachingBubbleDto';

export type SerializableUiConfigsStore = Pick<
    UIConfigsStore,
    | 'theme'
    | 'locale'
    | 'allowlist'
    | 'announcements'
    | 'teachingBubbles'
    | 'isSidebarCollapsed'
    | 'utterancesViewOptions'
    | 'skippedTeachingBubbles'
    | 'isCustomTextFilesViewerCollapsed'
    | 'qnaMakerEditKbOptionalColumnsVisibility'
    | 'qnaMakerReviewSuggestionsOptionalColumnsVisibility'
    | 'isAmlTeachingBubbleHidden'
>;

export type Announcement = { message: string; hidden: boolean; portal: Portal };

type UtterancesViewOptions = {
    hideEntityNames: boolean;
    hidePredictions: boolean;
    hideIntentDetailedScores: boolean;
    hideRegexAndPrebuilt: boolean;
    hideSubEntities: boolean;
};

const storage = getStorage('localStorage');
const getAzureAppConfigAnnouncements = () => tryParseJson<Announcement[]>(window.announcementMessages) ?? [];

export type OptionalColumnKey = 'context' | 'metadata';
export type OptionalColumnVisibility = { key: OptionalColumnKey; isVisible: boolean };

/**
 * Represents the root node of the LUIS portal UI state tree.
 */
export class UIConfigsStore {
    @observable public portal: Portal;

    @observable public locale: Locale;

    @observable public theme: 'light' | 'dark';

    @observable public isSidebarCollapsed: boolean;

    @observable public landingPageActivePivot: LandingPagePivot;

    @observable public announcements: Announcement[];

    @observable public allowlist: Allowlist[];

    @observable public isSwitchAccountModalHidden: boolean;

    @observable public selectedResourceId: string;

    @observable public selectedSubscriptionId: string;

    @observable public isEntitiesMigrationModalOpen: boolean;

    @observable public isNoAccountTeachingBubbleHidden: boolean;

    @observable public isCustomTextFilesViewerCollapsed: boolean;

    @observable public utterancesViewOptions: UtterancesViewOptions;

    @observable public isEditUtteranceDisclaimerModalHidden: boolean;

    @observable public currentActiveGlobalBlade: GlobalHeaderBladeType;

    @observable public languageStudioSignInTeachingBubbleHidden: boolean;

    @observable public currentActiveSettingTab: GlobalSettingsBladeTabsType;

    @observable public isCompositeEntitiesMigrationCoachMarkVisible: boolean;

    @observable public isCluMigrationModalHidden: boolean;

    @observable public isCluMigrationLuisApplicationsnModalHidden: boolean;

    @observable public isCluMigrationNextStepsModalHidden: boolean;

    @observable public isAmlTeachingBubbleHidden: boolean;

    @observable public teachingBubbles: { [key in TeachingBubblesKeys]?: boolean };

    @observable public skippedTeachingBubbles: { [key in TeachingBubblesKeys]?: boolean };

    @observable public qnaMakerEditKbOptionalColumnsVisibility: OptionalColumnVisibility[];

    @observable public qnaMakerReviewSuggestionsOptionalColumnsVisibility: OptionalColumnVisibility[];

    @observable public cluSchemaBuildPageActivePivot: CluBuildSchemaPagePivot;

    constructor() {
        const session: SerializableUiConfigsStore = tryParseJson(storage.getData(LuisConstants.StorageKeys.PortalSettingsStorageKey));

        this.portal = getPortalFromUrl();
        this.currentActiveGlobalBlade = null;
        this.isCluMigrationModalHidden = true;
        this.theme = session?.theme ?? 'light';
        this.isSwitchAccountModalHidden = true;
        this.isAmlTeachingBubbleHidden = session?.isAmlTeachingBubbleHidden ?? false;
        this.landingPageActivePivot = 'featured';
        this.isEntitiesMigrationModalOpen = false;
        this.isNoAccountTeachingBubbleHidden = true;
        this.currentActiveSettingTab = 'directories';
        this.allowlist = getAzureAppConfigAllowlist();
        this.isCluMigrationNextStepsModalHidden = true;
        this.isEditUtteranceDisclaimerModalHidden = false;
        this.teachingBubbles = session?.teachingBubbles ?? {};
        this.languageStudioSignInTeachingBubbleHidden = false;
        this.isCluMigrationLuisApplicationsnModalHidden = true;
        this.isCompositeEntitiesMigrationCoachMarkVisible = false;
        this.isSidebarCollapsed = session?.isSidebarCollapsed ?? false;
        this.locale = session?.locale ?? { code: 'en', name: 'English' };
        this.skippedTeachingBubbles = session?.skippedTeachingBubbles ?? {};
        this.isCustomTextFilesViewerCollapsed = session?.isCustomTextFilesViewerCollapsed ?? false;
        this.announcements = this._mergeAnnouncements(session?.announcements || [], getAzureAppConfigAnnouncements());
        this.utterancesViewOptions = session?.utterancesViewOptions ?? {
            hideEntityNames: false,
            hidePredictions: false,
            hideSubEntities: false,
            hideRegexAndPrebuilt: false,
            hideIntentDetailedScores: true
        };
        this.qnaMakerEditKbOptionalColumnsVisibility = session?.qnaMakerEditKbOptionalColumnsVisibility ?? [
            { key: 'context', isVisible: true },
            { key: 'metadata', isVisible: true }
        ];
        this.qnaMakerReviewSuggestionsOptionalColumnsVisibility = session?.qnaMakerReviewSuggestionsOptionalColumnsVisibility ?? [
            { key: 'context', isVisible: false },
            { key: 'metadata', isVisible: false }
        ];
    }

    /**
     * Serializes the given store to the local storage.
     */
    private _serialize() {
        storage.setData(LuisConstants.StorageKeys.PortalSettingsStorageKey, JSON.stringify(this.serializable));
    }

    /**
     * Returns a serializable version of the store
     * used when storing data in local storage.
     */
    @computed
    get serializable(): SerializableUiConfigsStore {
        return {
            theme: this.theme,
            locale: this.locale,
            allowlist: this.allowlist,
            announcements: this.announcements,
            teachingBubbles: this.teachingBubbles,
            isAmlTeachingBubbleHidden: this.isAmlTeachingBubbleHidden,
            skippedTeachingBubbles: this.skippedTeachingBubbles,
            isSidebarCollapsed: this.isSidebarCollapsed,
            utterancesViewOptions: this.utterancesViewOptions,
            isCustomTextFilesViewerCollapsed: this.isCustomTextFilesViewerCollapsed,
            qnaMakerEditKbOptionalColumnsVisibility: this.qnaMakerEditKbOptionalColumnsVisibility,
            qnaMakerReviewSuggestionsOptionalColumnsVisibility: this.qnaMakerReviewSuggestionsOptionalColumnsVisibility
        };
    }

    /**
     * Sets the announcementBanners array.
     */
    @action
    private _mergeAnnouncements(localStorageAnnouncements: Announcement[], windowAnnouncements: Announcement[]): Announcement[] {
        const newAnnouncements = windowAnnouncements
            .filter(
                wA =>
                    localStorageAnnouncements.find(
                        lA => lA.message === wA.message && lA.portal.kind === wA.portal.kind && lA.portal?.subPath === wA.portal?.subPath
                    ) === undefined
            )
            .map(wA => ({ ...wA, hidden: false }));

        return [...localStorageAnnouncements, ...newAnnouncements];
    }

    /**
     * Sets the current active global blade.
     */
    @action
    public setCurrentActiveGlobalBlade(newBlade: GlobalHeaderBladeType): void {
        this.currentActiveGlobalBlade = newBlade;
    }

    /**
     * Sets the current active global blade.
     */
    @action
    public setCurrentActiveSettingTab(newTab: GlobalSettingsBladeTabsType): void {
        this.currentActiveSettingTab = newTab;
    }

    /**
     * Sets the visibility state of the composite entities migration modal.
     */
    @action
    public setIsEntitiesMigrationModalOpen(isVisible: boolean): void {
        this.isEntitiesMigrationModalOpen = isVisible;
    }

    /**
     * Sets the collapse state of the sidebar.
     *
     * @param isSidebarClosed the collapse state of the sidebar.
     */
    @action
    public setIsSidebarCollapsed(isSidebarCollapsed: boolean) {
        this.isSidebarCollapsed = isSidebarCollapsed;
        this._serialize();
    }

    /**
     * Sets the collapse state of the customText file viewer collapsed.
     *
     * @param isCustomTextFilesViewerCollapsed the collapse state of the sidebar.
     */
    @action
    public setIsCustomTextFilesViewerCollapsed(isCustomTextFilesViewerCollapsed: boolean) {
        this.isCustomTextFilesViewerCollapsed = isCustomTextFilesViewerCollapsed;
        this._serialize();
    }

    /**
     * Sets the visibility state of the composite entities migration coach mark.
     */
    @action
    public setIsCompositeEntitiesMigrationCoachMarkVisible(isVisible: boolean): void {
        this.isCompositeEntitiesMigrationCoachMarkVisible = isVisible;
    }

    /**
     * Sets the visibility state of the clu migration modal.
     *
     * @param isCluMigrationModalHidden the visibility state of the modal.
     */
    @action
    public setIsCluMigrationModalHidden(isCluMigrationModalHidden: boolean) {
        this.isCluMigrationModalHidden = isCluMigrationModalHidden;
    }

    /**
     * Sets the visibility state of the clu migration migrating luis applications modal.
     *
     * @param isCluMigrationLuisApplicationsnModalHidden the visibility state of the modal.
     */
    @action
    public setisCluMigrationLuisApplicationsnModalHidden(isCluMigrationLuisApplicationsnModalHidden: boolean) {
        this.isCluMigrationLuisApplicationsnModalHidden = isCluMigrationLuisApplicationsnModalHidden;
    }

    /**
     * Sets the visibility state of the clu migration next steps modal.
     *
     * @param isCluMigrationNextStepsModalHidden the visibility state of the modal.
     */
    @action
    public setIsCluMigrationNextStepsModalHidden(isCluMigrationNextStepsModalHidden: boolean) {
        this.isCluMigrationNextStepsModalHidden = isCluMigrationNextStepsModalHidden;
    }

    /**
     * Sets the landing page active pivot.
     *
     * @param landingPagePivot the active pivot of the landing page.
     */
    @action
    public setLandingPageActivePivot(landingPagePivot: LandingPagePivot) {
        this.landingPageActivePivot = landingPagePivot;
    }

    /**
     * The locale to set as the current one.
     *
     * @param locale The locale to set.
     */
    @action
    public setLocale(locale: Locale) {
        this.locale = locale;
        logLocaleChanged(locale.name);
        this._serialize();
    }

    /**
     * The theme to set as the current one.
     *
     * @param theme The theme to set.
     */
    @action
    public setTheme(theme: UIConfigsStore['theme']) {
        this.theme = theme;
        this._serialize();
    }

    /**
     * Sets a specific teach bubble as already viewed
     * or not based on the given value.
     *
     * @param name The teaching bubble name.
     * @param value Whether the bubble was already
     * viewed or not.
     */
    @action
    public setTeachingBubble(name: TeachingBubblesKeys, value: boolean) {
        this.teachingBubbles[name] = value;
        this._serialize();
    }

    /**
     * Sets a specific teach bubble as already skipped
     * or not based on the given value.
     *
     * @param name The teaching bubble name.
     * @param value Whether the bubble was already
     * skipped or not.
     */
    @action
    public setSkippedTeachingBubble(name: TeachingBubblesKeys, value: boolean) {
        this.skippedTeachingBubbles[name] = value;
        this._serialize();
    }

    /**
     * Sets the view options to the input object.
     *
     * @param utterancesViewOptions The view options object to set..
     */
    @action
    public setUtterancesViewOptions(utterancesViewOptions: UIConfigsStore['utterancesViewOptions']) {
        this.utterancesViewOptions = utterancesViewOptions;
        this._serialize();
    }

    /**
     * Sets the hidden state of the edit utterance disclaimer modal.
     *
     * @param isEditUtteranceDisclaimerHidden the hidden state of the
     * edit utterance disclaimer modal.
     */
    @action
    public setIsEditUtteranceDisclaimerModalHidden(isEditUtteranceDisclaimerModalHidden: boolean) {
        this.isEditUtteranceDisclaimerModalHidden = isEditUtteranceDisclaimerModalHidden;
        this._serialize();
    }

    /**
     * Hides the announcement banner of the given index.
     */
    @action
    public hideAnnouncement(index: number) {
        this.announcements[index].hidden = true;
        this.announcements = this.announcements.slice();
        this._serialize();
    }

    /**
     * Determines if the switch account modal is hidden or not.
     */
    @action
    public setIsSwitchAccountModalHidden(isHidden: boolean) {
        this.isSwitchAccountModalHidden = isHidden;
    }

    /**
     * Sets the default value of resource id and subscription id for switch account modal.
     */
    @action
    public setIsSwitchAccountModalProps(isHidden: boolean, resourceId: string, subscriptionId: string) {
        this.isSwitchAccountModalHidden = isHidden;
        this.selectedResourceId = resourceId;
        this.selectedSubscriptionId = subscriptionId;
    }

    /**
     * Determines whether the no account teaching bubble is hidden or not.
     */
    @action
    public setIsNoAccountTeachingBubbleHidden(isHidden: boolean) {
        this.isNoAccountTeachingBubbleHidden = isHidden;
    }

    /**
     * Determines whether the language studio sign-in teaching bubble is hidden or not.
     */
    @action
    public setLanguageStudioSignInTeachingBubbleHidden(isHidden: boolean) {
        this.languageStudioSignInTeachingBubbleHidden = isHidden;
    }

    /**
     * Sets the current clu build schema active pivot.
     */
    @action
    public setCluBuildSchemaPageActivePivot(activePivot: CluBuildSchemaPagePivot): void {
        this.cluSchemaBuildPageActivePivot = activePivot;
    }

    /**
     * Hide Aml teaching bubble
     */
    @action
    public setAmlTeachingBubbleHidden() {
        this.isAmlTeachingBubbleHidden = true;
        this._serialize();
    }

    /**
     * Recalculates the portal object with the new location.
     */
    @action
    public updatePortal() {
        this.portal = getPortalFromUrl();
    }

    @action
    public toggleQnaMakerEditKbOptionalColumnsVisibility(key: string) {
        this.qnaMakerEditKbOptionalColumnsVisibility = this.qnaMakerEditKbOptionalColumnsVisibility.map(o => ({
            ...o,
            isVisible: o.key === key ? !o.isVisible : o.isVisible
        }));
        this._serialize();
    }

    @action
    public toggleQnaMakerReviewSuggestionsOptionalColumnsVisibility(key: string) {
        this.qnaMakerReviewSuggestionsOptionalColumnsVisibility = this.qnaMakerReviewSuggestionsOptionalColumnsVisibility.map(o => ({
            ...o,
            isVisible: o.key === key ? !o.isVisible : o.isVisible
        }));
        this._serialize();
    }
}
